import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useSeo } from "@/hooks/useSeo";
import { SITE_URL } from "@/lib/seoMeta";
import { IMAGES } from "@/lib/site";
import { useLang } from "@/context/LanguageContext";
import { loc, levelLabel } from "@/lib/localize";
import { Breadcrumbs } from "@/components/common/Breadcrumbs";
import { Button } from "@/components/ui/button";
import { Clock, ArrowRight, AlertTriangle } from "lucide-react";

export default function EducationArticle() {
  const { slug } = useParams();
  const { L, isAr } = useLang();
  const { data: lesson, isLoading, isError } = useQuery({
    queryKey: ["edu", slug],
    queryFn: async () => (await api.get(`/education/${slug}`)).data,
    retry: false,
  });
  const { data: all = [] } = useQuery({ queryKey: ["education"], queryFn: async () => (await api.get("/education")).data });
  useSeo({ title: lesson && (loc(lesson, "seo_title", isAr) || loc(lesson, "title", isAr)), description: lesson && (loc(lesson, "seo_description", isAr) || loc(lesson, "excerpt", isAr)), image: lesson?.featured_image || IMAGES.skyline, canonical: `${SITE_URL}/education/${slug}`, locale: isAr ? "ar_AE" : "en_US" });

  if (isLoading) return <div className="container-tight py-32 text-center text-subtle">...</div>;
  if (isError || !lesson) return (
    <div className="container-tight py-32 text-center">
      <h1 className="font-heading text-2xl font-semibold text-ink">404</h1>
      <Button asChild className="mt-6 bg-finblue hover:bg-finblueDark"><Link to="/education">{L.education.cta}</Link></Button>
    </div>
  );

  const idx = all.findIndex((l) => l.slug === slug);
  const prev = idx > 0 ? all[idx - 1] : null;
  const next = idx >= 0 && idx < all.length - 1 ? all[idx + 1] : null;

  return (
    <article className="pb-20">
      <div className="container-tight pt-10">
        <Breadcrumbs items={[{ label: L.common.home, to: "/" }, { label: L.nav.sub.education[0], to: "/education" }, { label: loc(lesson, "title", isAr) }]} />
      </div>
      <header className="container-tight max-w-3xl">
        <span className="overline">{levelLabel(lesson.level, isAr)}</span>
        <h1 className="mt-3 font-heading text-3xl font-bold leading-tight text-ink lg:text-4xl">{loc(lesson, "title", isAr)}</h1>
        <p className="mt-3 flex items-center gap-1.5 text-sm text-subtle"><Clock className="h-4 w-4" /> {lesson.reading_time} {L.common.min}</p>
      </header>

      <div className="container-tight mt-10 max-w-3xl">
        <div className="article-body" dangerouslySetInnerHTML={{ __html: loc(lesson, "body", isAr) }} />
        <div className="mt-8 flex gap-3 rounded-lg border border-hair bg-canvas p-4">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
          <p className="text-xs leading-relaxed text-subtle">{isAr ? "محتوى تعليمي فقط. ليس نصيحة استثمارية. ينطوي التداول على مخاطر كبيرة." : "Educational content only. Not investment advice. Trading involves significant risk."}</p>
        </div>

        <div className="mt-10 grid gap-4 sm:grid-cols-2">
          {prev ? (
            <Link to={`/education/${prev.slug}`} className="rounded-xl border border-hair bg-white p-5 shadow-sm transition-shadow hover:shadow-md">
              <span className="text-xs uppercase tracking-wide text-subtle">{isAr ? "السابق" : "Previous"}</span>
              <p className="mt-1 font-heading font-semibold text-ink">{loc(prev, "title", isAr)}</p>
            </Link>
          ) : <div />}
          {next && (
            <Link to={`/education/${next.slug}`} className="rounded-xl border border-hair bg-white p-5 text-end shadow-sm transition-shadow hover:shadow-md">
              <span className="text-xs uppercase tracking-wide text-subtle">{isAr ? "التالي" : "Next"}</span>
              <p className="mt-1 flex items-center justify-end gap-1 font-heading font-semibold text-ink">{loc(next, "title", isAr)} <ArrowRight className="h-4 w-4 rtl:rotate-180" /></p>
            </Link>
          )}
        </div>
      </div>
    </article>
  );
}
